import { MAX_SCHEDULE_BUFFER_MINUTES, ScheduleBuffer } from "@gettactic/api";

const clamp = (minutes: number | undefined) =>
  Math.min(Math.max(Math.round(minutes ?? 0), 0), MAX_SCHEDULE_BUFFER_MINUTES);

/**
 * The `buffer` a reservation request carries, from what the pickers hold.
 *
 * Both sides at "None" return `null`, which on a create means "omit it" and on
 * an update means "hand the buffer back to the office". The pickers add time on
 * top of the office buffer; they can never book below it, so an explicit
 * zero pair — which core reads as "this reservation has no buffer at all" — is
 * deliberately never sent.
 */
export const toScheduleBufferRequest = (
  buffer: ScheduleBuffer | undefined | null
): ScheduleBuffer | null => {
  const before = clamp(buffer?.before_minutes);
  const after = clamp(buffer?.after_minutes);

  if (before === 0 && after === 0) return null;
  return { before_minutes: before, after_minutes: after };
};

const isZeroPair = (buffer: ScheduleBuffer | null | undefined): boolean =>
  !!buffer && buffer.before_minutes === 0 && buffer.after_minutes === 0;

/**
 * The `buffer` an update sends. Both pickers at "None" hand the buffer back to
 * the office, as on a create — unless the reservation is stored with an
 * explicit zero pair, which is how an approver removes its buffer entirely.
 * The pickers cannot tell that apart from "none", so an edit that leaves them
 * alone keeps the stored pair rather than silently restoring the office one.
 *
 * `undefined` is a form that never carried the buffer at all (the recurring
 * edit dialog, say), not a choice: the stored buffer stays as it is.
 */
export const toScheduleBufferUpdateRequest = (
  buffer: ScheduleBuffer | undefined | null,
  stored: ScheduleBuffer | undefined | null
): ScheduleBuffer | null => {
  if (buffer === undefined) return stored ?? null;
  const requested = toScheduleBufferRequest(buffer);
  if (requested) return requested;
  return isZeroPair(stored) ? { before_minutes: 0, after_minutes: 0 } : null;
};
